import React, { useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import { toast } from 'react-toastify'
import StreamingManagementService from '../../services/StreamingManagementService'

// ─── Force Stop Modal ─────────────────────────────────────────────────────────

interface ForceStopModalProps {
  open: boolean
  streamingId: string | null
  onClose: () => void
  onSuccess: () => void
}

export function ForceStopModal({ open, streamingId, onClose, onSuccess }: ForceStopModalProps) {
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  const handleConfirm = async () => {
    if (!streamingId) return
    setLoading(true)
    setErrors([])
    try {
      const res = await StreamingManagementService.forceStop(streamingId)
      if (res?.data?.errors?.length) {
        setErrors(res.data.errors)
      }
      toast.success('Streaming parado com sucesso.')
      onSuccess()
      onClose()
    } catch {
      toast.error('Erro ao forçar parada do streaming.')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (loading) return
    setErrors([])
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Forçar Parada</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Tem certeza que deseja parar forçadamente este streaming? Esta ação não pode ser desfeita
          automaticamente.
        </DialogContentText>
        {errors.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Streaming parado, mas com avisos:
            <List dense disablePadding>
              {errors.map((e, i) => (
                <ListItem key={i} disableGutters>
                  <ListItemText primary={e} />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Forçar Parada
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Retry Modal ──────────────────────────────────────────────────────────────

interface RetryModalProps {
  open: boolean
  streamingId: string | null
  onClose: () => void
  onSuccess: () => void
}

export function RetryModal({ open, streamingId, onClose, onSuccess }: RetryModalProps) {
  const [loading, setLoading] = useState(false)

  const handleConfirm = async () => {
    if (!streamingId) return
    setLoading(true)
    try {
      await StreamingManagementService.retry(streamingId)
      toast.success('Job de retry despachado. Acompanhe no monitor de canais.')
      onSuccess()
      onClose()
    } catch {
      toast.error('Erro ao tentar novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Tentar Novamente</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Deseja re-despachar o job de streaming? O processo voltará para o status{' '}
          <strong>Criado</strong> enquanto o job executa.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          color="warning"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Tentar Novamente
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Dismiss Modal ────────────────────────────────────────────────────────────

interface DismissModalProps {
  open: boolean
  streamingId: string | null
  onClose: () => void
  onSuccess: () => void
}

export function DismissModal({ open, streamingId, onClose, onSuccess }: DismissModalProps) {
  const [loading, setLoading] = useState(false)

  const handleConfirm = async () => {
    if (!streamingId) return
    setLoading(true)
    try {
      await StreamingManagementService.dismiss(streamingId)
      toast.success('Streaming descartado.')
      onSuccess()
      onClose()
    } catch {
      toast.error('Erro ao descartar streaming.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Descartar Streaming</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Tem certeza que deseja descartar este streaming? Ele será marcado como{' '}
          <strong>descartado</strong> e removido da fila de atenção.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          color="inherit"
          variant="outlined"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Descartar
        </Button>
      </DialogActions>
    </Dialog>
  )
}
